import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Dialog from './Dialog'
import Select from './Select'
import InputField from './InputField'
import GridIconButton from './GridIconButton'
import SortableTableHead from './SortableTableHead'
import { colours, titlesImpostazioni } from '../settings/settings'
import { sortAuto } from '../hooks/sort-functions'
import { formatBodyField, formatTabelloneName } from '../hooks/utils-functions'
import {
  useGetTabelloniQuery,
  useCreateTabelloneMutation,
  useUpdateTabelloneMutation,
  useDeleteTabelloneMutation,
  useCreateTabelloneSfidaMutation,
  useUpdateTabelloneSfidaMutation,
  useDeleteTabelloneSfidaMutation,
} from '../hooks/api-slice'

const columns = [
  { label: 'Fase', sortable: true, sortingField: 'fase' },
  { label: 'Sfida', sortable: true, sortingField: 'sfida' },
  { label: 'Sfida vincente', sortable: false },
  { label: 'Sfida perdente', sortable: false },
  { label: 'Opzioni', sortable: false },
]

const DialogTabelloni = ({ open, handleClose }) => {
  const sortingColumn = useSelector(state => state.sorting.tabelloni?.column)
  const sortingAscending = useSelector(
    state => state.sorting.tabelloni?.ascending,
  )

  const [createTabelloneMutation] = useCreateTabelloneMutation()
  const [updateTabelloneMutation] = useUpdateTabelloneMutation()
  const [deleteTabelloneMutation] = useDeleteTabelloneMutation()
  const [createTabelloneSfidaMutation] = useCreateTabelloneSfidaMutation()
  const [updateTabelloneSfidaMutation] = useUpdateTabelloneSfidaMutation()
  const [deleteTabelloneSfidaMutation] = useDeleteTabelloneSfidaMutation()

  const [selectedTabelloneId, setSelectedTabelloneId] = useState()
  const [sfideTabellone, setSfideTabellone] = useState()
  const [editingTabelloneId, setEditingTabelloneId] = useState()
  const [addingTabellone, setAddingTabellone] = useState()
  const [newSquadre, setNewSquadre] = useState()
  const [newNome, setNewNome] = useState()

  const [editingSfidaId, setEditingSfidaId] = useState()
  const [addingSfida, setAddingSfida] = useState()
  const [newSfida, setNewSfida] = useState()

  const cSetNewSfida = (field, value) =>
    setNewSfida({ ...newSfida, [field]: formatBodyField(value) })

  const { data: tabelloni } = useGetTabelloniQuery()

  useEffect(() => {
    if (!tabelloni?.map(x => x._id).includes(selectedTabelloneId))
      setSelectedTabelloneId(tabelloni?.[0]?._id)
  }, [selectedTabelloneId, tabelloni])

  useEffect(() => {
    setSfideTabellone(
      sortAuto(
        tabelloni
          ?.find(x => x._id === selectedTabelloneId)
          ?.sfide?.map(x => ({ ...x, fase: +x.fase, sfida: +x.sfida })) || [],
        sortingColumn,
        sortingAscending,
      ),
    )
  }, [selectedTabelloneId, sortingAscending, sortingColumn, tabelloni])

  useEffect(() => {
    if (addingSfida) setAddingTabellone()
  }, [addingSfida])

  useEffect(() => {
    if (addingTabellone) setAddingSfida()
  }, [addingTabellone])

  const clearNewTabellone = () => {
    setEditingTabelloneId()
    setAddingTabellone()
    setNewSquadre()
    setNewNome()
  }

  const clearNewSfida = () => {
    setEditingSfidaId()
    setAddingSfida()
    setNewSfida()
  }

  const addNewTabellone = async () => {
    const body = { squadre: newSquadre, nome: newNome }
    const result = await createTabelloneMutation(body)
    if (!result?.error) clearNewTabellone()
  }

  const editTabellone = async () => {
    const result = await updateTabelloneMutation({
      id: selectedTabelloneId,
      squadre: newSquadre,
      nome: newNome,
    })
    if (!result?.error) clearNewTabellone()
  }

  const deleteTabellone = async () => {
    await deleteTabelloneMutation(selectedTabelloneId)
  }

  const addNewSfida = async () => {
    const result = await createTabelloneSfidaMutation({
      selectedTabelloneId,
      ...newSfida,
    })
    if (!result?.error) clearNewSfida()
  }

  const editSfida = async id => {
    const result = await updateTabelloneSfidaMutation({
      selectedTabelloneId,
      id,
      ...newSfida,
    })
    if (!result?.error) clearNewSfida()
  }

  const deleteSfida = async id => {
    await deleteTabelloneSfidaMutation({ selectedTabelloneId, id })
  }

  useEffect(() => {
    clearNewTabellone()
    clearNewSfida()
  }, [open])

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      title={titlesImpostazioni.titleTabelloni}
      maxWidth='lg'
      stretchHeight
    >
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid size={12}>
          <b>Sfida</b>: turno a eliminazione (partita secca o andata/ritorno).
          <br />
          <b>Fase</b>: insieme di sfide dello stesso turno.
          <br />
          <br />
          <b>Esempio</b>: in un tabellone a 8 squadre la fase 1 sono i quarti di
          finale (sfide 1-2-3-4), la fase 2 sono le semifinali (sfide 5-6) e la
          fase 3 sono le finali (sfide 7-8).
          <br />
          <br />
          Per ogni sfida indicare a quale sfida accedono la squadra vincente e
          la squadra perdente.
          <br />
          <br />
          <b>Tabelloni doppi</b>: è possibile creare anche due tabelloni
          separati nella stessa categoria (per esempio, uno OVEST e uno EST). Di
          fatto, è come creare un unico tabellone, con la differenza che le
          ultime due fasi avranno 4 sfide ciascuna invece che 2 (ci saranno
          infatti 2+2 semifinali e 2+2 finali).
        </Grid>
        <Grid container alignItems='center' sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Select
              label='Tabellone'
              value={selectedTabelloneId}
              options={tabelloni?.map(x => x._id)}
              optionLabels={tabelloni?.map(formatTabelloneName)}
              onChange={setSelectedTabelloneId}
            />
          </Grid>
          <GridIconButton
            type='add'
            size={{ xs: 12, md: 'grow' }}
            onClick={() => {
              setEditingTabelloneId()
              setAddingTabellone(true)
              setNewSquadre()
              setNewNome()
            }}
            text='Nuovo'
          />
          <GridIconButton
            type='edit'
            size={{ xs: 12, md: 'grow' }}
            onClick={() => {
              const selectedTabellone = tabelloni?.find(
                x => x._id === selectedTabelloneId,
              )
              setEditingTabelloneId(selectedTabelloneId)
              setAddingTabellone()
              setNewSquadre(selectedTabellone?.squadre)
              setNewNome(selectedTabellone?.nome)
            }}
            text='Rinomina'
          />
          <GridIconButton
            type='delete'
            size={{ xs: 12, md: 'grow' }}
            onClick={deleteTabellone}
            text='Elimina'
          />
        </Grid>
        {(!!addingTabellone && (
          <Grid container alignItems='center' sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, md: 2 }}>
              <center>
                <InputField
                  id='squadre'
                  label='Squadre'
                  value={newSquadre}
                  onChange={setNewSquadre}
                  type='number'
                />
              </center>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <center>
                <InputField
                  id='nome'
                  label='Nome'
                  value={newNome}
                  onChange={setNewNome}
                />
              </center>
            </Grid>
            <GridIconButton
              type='save'
              size={{ xs: 6, md: 1 }}
              onClick={addNewTabellone}
            />
            <GridIconButton
              type='clear'
              size={{ xs: 6, md: 1 }}
              onClick={clearNewTabellone}
            />
          </Grid>
        )) ||
          (!!editingTabelloneId &&
            editingTabelloneId === selectedTabelloneId && (
              <Grid container alignItems='center' sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <center>
                    <InputField
                      id='squadre'
                      label='Squadre'
                      value={newSquadre}
                      onChange={setNewSquadre}
                      type='number'
                      mode='update'
                    />
                  </center>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <center>
                    <InputField
                      id='nome'
                      label='Nome'
                      value={newNome}
                      onChange={setNewNome}
                      mode='update'
                    />
                  </center>
                </Grid>
                <GridIconButton
                  type='save'
                  size={{ xs: 6, md: 1 }}
                  onClick={editTabellone}
                />
                <GridIconButton
                  type='clear'
                  size={{ xs: 6, md: 1 }}
                  onClick={clearNewTabellone}
                />
              </Grid>
            )) || (
            <>
              {selectedTabelloneId && (
                <GridIconButton
                  type='add'
                  size={{ xs: 12, md: 6 }}
                  onClick={() => {
                    setEditingSfidaId()
                    setAddingSfida(true)
                    setNewSfida({})
                  }}
                  text='Nuova sfida'
                />
              )}
              {!!addingSfida && (
                <Grid
                  container
                  spacing={2}
                  alignItems='center'
                  sx={{ width: '100%' }}
                >
                  <Grid size={{ xs: 12, md: 2 }}>
                    <center>
                      <InputField
                        id='fase'
                        value={newSfida?.fase}
                        onChange={i => cSetNewSfida('fase', i)}
                        autoFocus
                        label={columns[0].label}
                        type='number'
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <center>
                      <InputField
                        id='sfida'
                        value={newSfida?.sfida}
                        onChange={i => cSetNewSfida('sfida', i)}
                        label={columns[1].label}
                        type='number'
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <center>
                      <InputField
                        id='sfidaVincente'
                        value={newSfida?.sfidaVincente}
                        onChange={i => cSetNewSfida('sfidaVincente', i)}
                        label={columns[2].label}
                        type='number'
                      />
                    </center>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <center>
                      <InputField
                        id='sfidaPerdente'
                        value={newSfida?.sfidaPerdente}
                        onChange={i => cSetNewSfida('sfidaPerdente', i)}
                        label={columns[3].label}
                        type='number'
                      />
                    </center>
                  </Grid>
                  <GridIconButton
                    type='save'
                    size={{ xs: 6, md: 1 }}
                    onClick={addNewSfida}
                  />
                  <GridIconButton
                    type='clear'
                    size={{ xs: 6, md: 1 }}
                    onClick={clearNewSfida}
                  />
                </Grid>
              )}
              {(sfideTabellone?.length === 0 && (
                <h4>Nessuna sfida trovata.</h4>
              )) || (
                <TableContainer component={Paper}>
                  <Table size='small'>
                    <SortableTableHead
                      table={sfideTabellone}
                      setTable={setSfideTabellone}
                      columns={columns}
                      tableName='tabelloni'
                    />
                    <TableBody>
                      {sfideTabellone?.map((s, index) => {
                        const cellProps = {
                          padding: 'checkbox',
                          style: {
                            borderBottomColor:
                              index < sfideTabellone.length - 1 &&
                              sfideTabellone[index].fase !==
                                sfideTabellone[index + 1].fase &&
                              colours.greyVeryDark,
                          },
                        }

                        return (
                          <TableRow
                            key={index}
                            style={{ backgroundColor: colours.white }}
                          >
                            {(editingSfidaId === s._id && (
                              <>
                                <TableCell {...cellProps}>
                                  <InputField
                                    id='fase'
                                    value={newSfida?.fase}
                                    onChange={i => cSetNewSfida('fase', i)}
                                    autoFocus
                                    label={columns[0].label}
                                    type='number'
                                    mode='update'
                                  />
                                </TableCell>
                                <TableCell {...cellProps}>
                                  <InputField
                                    id='sfida'
                                    value={newSfida?.sfida}
                                    onChange={i => cSetNewSfida('sfida', i)}
                                    label={columns[1].label}
                                    type='number'
                                    mode='update'
                                  />
                                </TableCell>
                                <TableCell {...cellProps}>
                                  <InputField
                                    id='sfidaVincente'
                                    value={newSfida?.sfidaVincente}
                                    onChange={i =>
                                      cSetNewSfida('sfidaVincente', i)
                                    }
                                    label={columns[2].label}
                                    type='number'
                                    mode='update'
                                  />
                                </TableCell>
                                <TableCell {...cellProps}>
                                  <InputField
                                    id='sfidaPerdente'
                                    value={newSfida?.sfidaPerdente}
                                    onChange={i =>
                                      cSetNewSfida('sfidaPerdente', i)
                                    }
                                    label={columns[3].label}
                                    type='number'
                                    mode='update'
                                  />
                                </TableCell>
                                <TableCell {...cellProps}>
                                  <Grid container sx={{ width: '100%' }}>
                                    <GridIconButton
                                      type='save'
                                      onClick={() => editSfida(s._id)}
                                    />
                                    <GridIconButton
                                      type='clear'
                                      onClick={clearNewSfida}
                                    />
                                  </Grid>
                                </TableCell>
                              </>
                            )) || (
                              <>
                                <TableCell {...cellProps}>{s.fase}</TableCell>
                                <TableCell {...cellProps}>{s.sfida}</TableCell>
                                <TableCell {...cellProps}>
                                  {s.sfidaVincente}
                                </TableCell>
                                <TableCell {...cellProps}>
                                  {s.sfidaPerdente}
                                </TableCell>
                                <TableCell {...cellProps}>
                                  <Grid container sx={{ width: '100%' }}>
                                    <GridIconButton
                                      type='edit'
                                      style={{ color: colours.blueDark }}
                                      onClick={() => {
                                        setEditingSfidaId(s._id)
                                        setAddingSfida()
                                        setNewSfida({
                                          fase: s.fase && +s.fase,
                                          sfida: s.sfida && +s.sfida,
                                          sfidaVincente:
                                            s.sfidaVincente && +s.sfidaVincente,
                                          sfidaPerdente:
                                            s.sfidaPerdente && +s.sfidaPerdente,
                                        })
                                      }}
                                    />
                                    <GridIconButton
                                      type='delete'
                                      onClick={() => deleteSfida(s._id)}
                                    />
                                  </Grid>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
      </Grid>
    </Dialog>
  )
}

export default DialogTabelloni
